import React, { useCallback, useMemo } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import WarningIcon from "@mui/icons-material/WarningRounded";
import Alert, { AlertProps } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import {
    gridClasses,
    GridColDef,
    GridEventListener,
    GridRenderCellParams,
    GridRowParams,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    useGridApiRef,
    GridGroupingColDefOverride,
    GridSortModel,
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
    GridValueGetterParams,
    GridDataGroupNode,
    GridAutoGeneratedGroupNode,
    GridTreeNode,
    GridRowId,
    GridGroupNode,
    GridSortCellParams,
    GridComparatorFn,
    GridValueFormatterParams,
} from "@mui/x-data-grid-premium";

import { StripedDataGrid, getGridLocaleText } from "../muiDataGrid";
import { CustomToolbar } from "./customToolbar";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";
import _forEach from "lodash/forEach";
import { Base } from "../../../framework/base";
import { useTranslation } from "react-i18next";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import {
    ExcelFormatterResult,
    exceljsPostProcess,
    ExceljsPostProcessParams,
} from "./excelJsProcessing";

const getGroupingModeMultipleColumnName = (name: string) =>
    `__row_group_by_columns_group_${name}__`;

const getFieldNameFromGroupingColumn = (name: string) =>
    (name ?? "").split("_").filter(Boolean).reverse()[0];

function getAllChildRowIds(
    rowNode: GridTreeNode | GridDataGroupNode | GridAutoGeneratedGroupNode,
    api: GridApiPremium
): GridRowId[] {
    if (rowNode.type === "group") {
        return rowNode.children
            .map((c) => api.getRowNode(c))
            .flatMap((n) => getAllChildRowIds(n, api));
    }

    return [rowNode.id];
}

export function getAllChildRows<T>(
    rowNode: GridTreeNode | GridDataGroupNode | GridAutoGeneratedGroupNode,
    api: GridApiPremium
): T[] {
    return getAllChildRowIds(rowNode, api)
        .map((id) => api.getRow<GroupedRow<T>>(id))
        .flatMap((r) => r?.rows ?? []);
}

export type GroupableDataGridColDef<T> = GridColDef<T> & {
    excelFormatter?: (value: any) => ExcelFormatterResult;
};

export interface GroupableDataGridRow {
    id: string;
    totals: Record<string, number>;
}

export interface GroupedRow<T> {
    id: string;
    warning?: string; // Generic field for displaying warning in the grouping col
    rows: (T & GroupableDataGridRow)[];
    totals: Record<string, number>;
}

interface DataGridProps<T> {
    noRowsText: string;
    noRowsSeverity?: AlertProps["severity"];
    detailsComponent: (props: any) => JSX.Element;
    detailsComponentProps?: any;
    columns: GroupableDataGridColDef<T>[];
    rows: (T & GroupableDataGridRow)[];
    groupingColDef: GridGroupingColDefOverride;
    getGroupLabel?: (value: any, field: string) => string;
    pinnedColumns?: string[];
    hiddenColumns?: string[];
    groupBy: (keyof T)[];
    groupingModeMultiple?: boolean;
    showGroupedRows?: boolean;
    onColumnOrderChange?: (val: string[]) => void;
    sortModel?: GridSortModel;
    onSortModelChange?: (val: GridSortModel) => void;
    useExcelPostProcess?: boolean;
    tableMaxHeight?: string;
}

export function GroupableDataGrid<T>({
    noRowsText,
    noRowsSeverity = "info",
    detailsComponent: DetailsComponent,
    detailsComponentProps = {},
    columns,
    rows,
    groupingColDef,
    getGroupLabel,
    pinnedColumns,
    hiddenColumns: propHiddenColumns,
    groupBy: propGroupBy,
    groupingModeMultiple: propGroupingModeMultiple = false,
    showGroupedRows = true,
    onColumnOrderChange,
    sortModel,
    onSortModelChange,
    useExcelPostProcess,
    tableMaxHeight = "100%",
}: DataGridProps<T>) {
    const { t } = useTranslation();

    const groupedRows: GroupedRow<T>[] = useMemo(
        () =>
            _map(
                _groupBy(
                    rows,
                    (r) => propGroupBy?.map((g) => r[g]).join("_") ?? null
                ),
                (arr, groupingKey) => ({
                    ...Object.fromEntries(
                        _map(
                            arr[0] ?? ({} as T & GroupableDataGridRow),
                            (v, k) =>
                                typeof v === "object"
                                    ? null
                                    : [
                                        k,
                                        Base.getUniqueItems(
                                            arr.map(
                                                (a) =>
                                                    a[
                                                          k as keyof (T &
                                                              GroupableDataGridRow)
                                                    ]
                                            )
                                        ).length === 1
                                            ? v
                                            : null,
                                    ]
                        ).filter(Boolean)
                    ),
                    ...Object.fromEntries(
                        propGroupBy?.map((g, i) => [
                            g,
                            groupingKey.split("_")[i] ?? null,
                        ]) ?? []
                    ),
                    id: groupingKey,
                    rows: arr,
                    totals: arr.reduce((acc, curr) => {
                        _forEach(curr.totals, (v, k) => {
                            if (!!v || v === 0) {
                                acc[k] ??= 0;
                                acc[k] += v;
                            }
                        });

                        return acc;
                    }, {}),
                })
            ),
        [rows, propGroupBy]
    );

    const groupBy = useMemo(
        () =>
            !showGroupedRows
                ? []
                : ((!!propGroupBy
                    ? propGroupBy.slice(0, -1)
                    : propGroupBy) as unknown as string[]),
        [propGroupBy, showGroupedRows]
    );

    // If grouped by only one column,
    // make sure groupingModeMultiple is true to render grouping column correctly
    const groupingModeMultiple = useMemo(
        () => propGroupingModeMultiple || groupBy?.length === 0,
        [propGroupingModeMultiple, groupBy]
    );

    // Add Footer row rendering to first column when 'showGroupedRows' is false
    // This is done here because grouping is disabled when grouped rows are hidden.
    const columnDef = useMemo(
        () =>
            showGroupedRows &&
            (!groupingModeMultiple || propGroupBy?.length > 1)
                ? columns
                : columns.map((c) => {
                    if (!!c.field && c.field === propGroupBy?.[0]) {
                        return {
                            ...c,
                            valueFormatter: (
                                params: GridValueFormatterParams<unknown>
                            ) => {
                                if (
                                    params.id ===
                                    GRID_AGGREGATION_ROOT_FOOTER_ROW_ID
                                ) {
                                    return t("common.total").toString();
                                }

                                if (!!c.valueFormatter) {
                                    return c.valueFormatter(params) as unknown;
                                }

                                return params.value;
                            },
                            renderCell: (params: GridRenderCellParams<T>) => {
                                if (
                                    !!params.formattedValue &&
                                    params?.rowNode?.type === "pinnedRow"
                                ) {
                                    return (
                                        <div className="font-weight-bold">
                                            {params.formattedValue}
                                        </div>
                                    );
                                }
                                const warning = params.api.getCellValue(
                                    params.id,
                                    "warning"
                                );
                                const warningIcon = (
                                    <Warning warning={warning} />
                                );

                                return (
                                    <>
                                        {warningIcon}
                                        {!!c.renderCell
                                            ? c.renderCell(params)
                                            : params.formattedValue}
                                    </>
                                );
                            },
                            width: 150,
                            flex: undefined,
                        } as GridColDef<GroupedRow<T>>;
                    }

                    return c;
                }),
        [columns, showGroupedRows, propGroupBy, t, groupingModeMultiple]
    );

    const apiRef = useGridApiRef();

    // Hidden because of grouping or as a propHiddenColumn
    const nonVisibleColumns = useMemo(
        () =>
            [
                propHiddenColumns ?? [],
                propHiddenColumns?.map((c) =>
                    getGroupingModeMultipleColumnName(c)
                ) ?? [],
                groupingModeMultiple ? groupBy : (propGroupBy as string[]),
            ].flat(),
        [groupingModeMultiple, groupBy, propGroupBy, propHiddenColumns]
    );

    const columnVisibility = useMemo(
        () => ({
            ...Object.fromEntries(
                columns.map((c) => [
                    c.field,
                    !nonVisibleColumns.includes(c.field),
                ])
            ),
            ...Object.fromEntries(
                groupBy.map((g) => {
                    const field = getGroupingModeMultipleColumnName(g);

                    return [field, !nonVisibleColumns.includes(field)];
                })
            ),
        }),
        [columns, groupBy, nonVisibleColumns]
    );

    const columnVisibilityModel = {
        [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
        ...columnVisibility,
    };

    const aggregationModel = useMemo(
        () =>
            Object.fromEntries([
                ...columns.map((c) => [c.field, "sum"]),
            ]) as Record<string, string>,
        [columns]
    );

    const pinnedColumnsModel = useMemo(
        () => ({
            left: [
                GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
                ...((propGroupBy as string[]) ?? []).flatMap((g) => [
                    getGroupingModeMultipleColumnName(g),
                    g,
                ]),
                ...(pinnedColumns ?? []),
            ],
        }),
        [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, propGroupBy, pinnedColumns]
    );

    const initialState: GridInitialStatePremium = {
        aggregation: {
            model: aggregationModel,
        },
        columns: {
            columnVisibilityModel,
        },
        sorting: {
            sortModel,
        },
        rowGrouping: {
            model: groupBy,
        },
        pinnedColumns: pinnedColumnsModel,
    };

    const onRowClick: GridEventListener<"rowClick"> = (params) => {
        const rowNode = apiRef.current.getRowNode(params.id);
        if (!rowNode) return;

        if (rowNode.type === "group") {
            apiRef.current.setRowChildrenExpansion(
                params.id,
                !rowNode.childrenExpanded
            );
        } else if (rowNode.type === "leaf") {
            apiRef.current.toggleDetailPanel(params.id);
        }
    };

    const getDetailPanelContent = useCallback(
        (params: GridRowParams) => {
            return (
                <DetailsComponent row={params.row} {...detailsComponentProps} />
            );
        },
        [DetailsComponent, detailsComponentProps]
    );

    const leafField = useMemo(
        () =>
            !!propGroupBy
                ? (propGroupBy[propGroupBy.length - 1] as string)
                : undefined,
        [propGroupBy]
    );

    const groupedColumnSortComparator = useCallback(
        (
            v1: any,
            v2: any,
            param1: GridSortCellParams<any>,
            param2: GridSortCellParams<any>
        ) => {
            let comparator: GridComparatorFn<any>;

            if (param1.rowNode.type === "group") {
                comparator = columns.find(
                    (c) =>
                        c.field ===
                        (
                            param1.rowNode as
                                | GridDataGroupNode
                                | GridAutoGeneratedGroupNode
                        ).groupingField
                )?.sortComparator;
            } else if (param1.rowNode.type === "leaf") {
                comparator = columns.find(
                    (c) => c.field === leafField
                )?.sortComparator;
            }

            if (!!comparator) {
                return comparator(v1, v2, param1, param2);
            }
        },
        [columns, leafField]
    );

    const handleColumnOrderChange = useCallback(() => {
        if (!!onColumnOrderChange && !!apiRef?.current?.exportState) {
            onColumnOrderChange(
                apiRef.current
                    .exportState()
                    .columns.orderedFields.filter((id) => columnVisibility[id])
            );
        }
    }, [onColumnOrderChange, apiRef, columnVisibility]);

    const excelPostProcess = useCallback(
        (params: ExceljsPostProcessParams) => {
            if (useExcelPostProcess) exceljsPostProcess(params, columnDef);
        },
        [useExcelPostProcess, columnDef]
    );

    return (
        <StripedDataGrid
            onColumnOrderChange={handleColumnOrderChange}
            disableRowGrouping={!showGroupedRows}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel}
            initialState={initialState}
            localeText={getGridLocaleText()}
            slots={{
                noRowsOverlay: () => (
                    <Alert severity={noRowsSeverity} sx={{ p: 2 }}>
                        {noRowsText}
                    </Alert>
                ),
                toolbar: CustomToolbar,
            }}
            slotProps={{
                toolbar: {
                    enableColumnSelector: false,
                    disableExpandAll: !showGroupedRows || groupBy?.length === 0,
                    enableHideEmptyColumns: false,
                    exceljsPostProcess: excelPostProcess
                },
            }}
            apiRef={apiRef}
            onRowClick={onRowClick}
            disableColumnMenu
            rowSelection={false}
            density="compact"
            hideFooter
            columns={columnDef}
            rows={groupedRows}
            disableColumnResize
            rowGroupingColumnMode={groupingModeMultiple ? "multiple" : "single"}
            rowGroupingModel={groupBy}
            groupingColDef={{
                hideDescendantCount: true,
                ...(groupingModeMultiple
                    ? {
                        valueGetter: (
                            params: GridValueGetterParams<
                                  GroupedRow<T>,
                                  string
                              >
                        ) => {
                            if (
                                params?.id ===
                                  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID
                            )
                                return;

                            if (params?.rowNode?.type === "group") {
                                const isGroupedCol =
                                      getGroupingModeMultipleColumnName(
                                          params.rowNode.groupingField
                                      ) === params.field;

                                if (isGroupedCol) {
                                    return params.rowNode.groupingKey;
                                }
                            }

                            if (
                                !!params.rowNode.parent &&
                                  params.rowNode.depth > 0
                            ) {
                                const api = params.api as GridApiPremium;
                                const ids = api.getRowGroupChildren({
                                    groupId: params.rowNode.parent,
                                });
                                const field = getFieldNameFromGroupingColumn(
                                    params?.field
                                );
                                const row: GroupedRow<T> = api.getRow(ids[0]);

                                return row[field as keyof GroupedRow<T>];
                            }
                        },
                    }
                    : {
                        leafField,
                    }),
                valueFormatter: (data) => {
                    const isFooterRow =
                        data?.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID;
                    const isSingleGroupingCol =
                        data?.field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD;

                    // If footer row and first or only grouping column
                    if (
                        isFooterRow &&
                        (isSingleGroupingCol ||
                            data?.field ===
                                getGroupingModeMultipleColumnName(
                                    (propGroupBy ?? [])[0] as string
                                ))
                    ) {
                        return t("common.total").toString();
                    }

                    let field = data.field;

                    if (isSingleGroupingCol) {
                        // If grouping mode is single,
                        // we need to find out the actual field from the id of the auto-generated row.
                        const id = data?.id.toString().split("/");

                        field =
                            id?.length > 1
                                ? id[id.length - 2].split("-").reverse()[0]
                                : leafField;
                    } else {
                        // If grouping mode is multiple,
                        // get field name for getting the value.
                        field = getFieldNameFromGroupingColumn(field);
                    }

                    const formatter = columns.find(
                        (c) => c.field === field
                    )?.valueFormatter;

                    // If the field has a formatter,
                    // apply that to return the data in correct format for excel.
                    if (!!formatter) {
                        return formatter(data) as unknown;
                    }

                    const value: string | undefined = data?.value;

                    return value;
                },
                width: groupingModeMultiple ? 150 : 250,
                sortComparator: groupedColumnSortComparator,
                renderCell: (params: GridRenderCellParams<GroupedRow<T>>) => (
                    <GroupingCol
                        params={params}
                        getGroupLabel={getGroupLabel}
                        groupingModeMultiple={groupingModeMultiple}
                        groupBy={propGroupBy ?? []}
                        leafField={leafField}
                    />
                ),
                ...(groupingModeMultiple ? {} : groupingColDef),
            }}
            pinnedColumns={pinnedColumnsModel}
            getDetailPanelContent={
                !!DetailsComponent ? getDetailPanelContent : undefined
            }
            getDetailPanelHeight={() => "auto"}
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            sx={{
                height: "initial",
                minHeight: "100px",
                maxHeight: tableMaxHeight,
                width: "100%",
                // Hide the "sum" label on headers
                [`& .${gridClasses.aggregationColumnHeaderLabel}`]: {
                    display: "none",
                },
                // disable cell selection style
                ".MuiDataGrid-cell:focus": {
                    outline: "none",
                },
                "& .MuiDataGrid-row:hover": {
                    cursor: "pointer",
                },
                // This is needed to make sticky positioning work for the detail panel
                "& .MuiDataGrid-detailPanel": {
                    overflow: "visible",
                },
                // Needed to display "no rows" overlay correctly
                ".MuiDataGrid-overlayWrapper": {
                    height: "auto !important",
                },
                ".MuiDataGrid-overlayWrapperInner": {
                    height: "auto !important",
                },
            }}
        />
    );
}

interface GroupingColProps<T> {
    params: GridRenderCellParams<GroupedRow<T>>;
    getGroupLabel: (value: any, field: string) => string;
    groupingModeMultiple: boolean;
    groupBy: (keyof T)[];
    leafField?: string;
}

export function GroupingCol<T>(props: GroupingColProps<T>) {
    const { params, getGroupLabel, groupingModeMultiple, groupBy, leafField } =
        props;
    const { id, value, row, formattedValue, api } = params;

    const warning = !!id ? api.getCellValue(id, "warning") : null;
    const warningIcon = <Warning warning={warning} />;

    if (params?.rowNode?.type === "group") {
        const field = groupingModeMultiple
            ? getFieldNameFromGroupingColumn(params?.field)
            : params.rowNode.groupingField;

        const isFirstGroupingCol =
            !groupingModeMultiple || groupBy[0] === field;

        return (
            <GroupToggle
                params={params}
                groupingField={field}
                warningIcon={isFirstGroupingCol ? warningIcon : null}
                label={
                    (!!getGroupLabel ? getGroupLabel(value, field) : value) ||
                    "-"
                }
            />
        );
    } else if (params?.rowNode?.type === "leaf") {
        const field = groupingModeMultiple
            ? getFieldNameFromGroupingColumn(params?.field)
            : leafField;
        const val = groupingModeMultiple ? value : formattedValue;
        const label = getGroupLabel
            ? getGroupLabel(
                val ?? row.rows[0][field],
                field
            )
            : (formattedValue as string);

        const isFirstGroupingCol =
            !groupingModeMultiple || groupBy[0] === field;

        return (
            <GroupLabel
                showToggle={false}
                marginLeft={
                    !!warning
                        ? 0
                        : groupingModeMultiple
                            ? 2
                            : params?.rowNode?.depth * 2
                }
                warningIcon={isFirstGroupingCol ? warningIcon : null}
                label={label}
            />
        );
    } else if (!!formattedValue && params?.rowNode?.type === "pinnedRow") {
        return <div className="font-weight-bold">{formattedValue}</div>;
    }
}

interface GroupToggleProps<T> {
    params: GridRenderCellParams<GroupedRow<T>>;
    groupingField?: string;
    label: string;
    marginLeft?: number;
    warningIcon?: React.JSX.Element;
}

export function GroupToggle<T>(props: GroupToggleProps<T>) {
    const { params, groupingField, label, marginLeft = 0, warningIcon } = props;
    const rowNode = params.rowNode as GridGroupNode;

    return (
        <GroupLabel
            showToggle={groupingField === rowNode.groupingField}
            open={rowNode.childrenExpanded ?? false}
            marginLeft={marginLeft}
            warningIcon={warningIcon}
            label={label ?? "-"}
        />
    );
}

interface GroupLabelProps {
    label: string;
    marginLeft?: number;
    warningIcon?: React.JSX.Element;
    showToggle: boolean;
    open?: boolean;
}

export function GroupLabel(props: GroupLabelProps) {
    const { showToggle, open, label, marginLeft = 0, warningIcon } = props;

    return (
        <Box ml={!showToggle ? marginLeft * 2 : 0} className="text-truncate">
            {showToggle ? (
                open ? (
                    <KeyboardArrowDownIcon sx={{ marginLeft }} />
                ) : (
                    <KeyboardArrowRightIcon sx={{ marginLeft }} />
                )
            ) : null}
            {warningIcon}
            <Box display="inline" title={label}>
                {label ?? ""}
            </Box>
        </Box>
    );
}

interface WarningProps {
    warning: string | null;
}

const Warning = ({ warning }: WarningProps) =>
    !!warning ? (
        <Tooltip title={warning}>
            <WarningIcon color="orange" sx={{ mr: 0.5 }} />
        </Tooltip>
    ) : null;
